/**
 * Layout ↔ editor bridge.
 *
 * The Layout solver bakes a Hug container's solved size back into its
 * authored width/height/depth. That write bypasses `commit()` (it is an
 * engine-measured output, not a user edit — see `_bakeContainerSizes`), so
 * nothing on the editor side would otherwise learn the inspector is showing
 * a stale number.
 *
 * The solver announces those writes through `TraceScene.onLayoutSizeBaked`,
 * a plain scene-level hook. This module is the only place that turns them
 * into the editor's `BEHAVIOR_CHANGED` CustomEvent — so `behaviors/layout.ts`
 * stays free of editor imports and a runtime/AR viewer dispatching nothing
 * is the default, not a `typeof window` accident.
 *
 * Installed automatically when an editor hands a scene to the shared
 * UndoManager (`undoManager.traceScene = scene`), which is the editor's one
 * "a scene is live and I own it" signal, together with the layout topology
 * sync (`layoutTopologySync.ts`). Consumers that don't use the undo manager
 * can call either directly.
 */

import type { TraceScene } from '../core/TraceScene';
import { Layout } from '../behaviors/layout';
import { dispatchBehaviorChanged } from './undo/editor-events';

/**
 * Refresh inspector panels when the solver writes a Hug container's size.
 * Returns the unsubscriber; call it before installing on a new scene.
 */
export function installLayoutInspectorRefresh(scene: TraceScene): () => void {
    return scene.onLayoutSizeBaked((container) => {
        // The hook only fires for containers the bake actually wrote, so
        // there is no no-op event to filter here. Dispatch is deferred a
        // microtask by the event layer, so a listener that re-reads the
        // scene sees the finished solve.
        dispatchBehaviorChanged({
            objectPath: container.getPath(),
            behaviorType: Layout.behaviorType,
            action: 'updated',
        });
    });
}
